import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Card,
  CardContent,
  Tabs,
  Tab
} from '@mui/material';
import {
  LocalGasStation,
  CreditCard,
  TrendingUp
} from '@mui/icons-material';
import { quikqAuthService } from '../services/quikqAuth';
import { quikqApiService } from '../services/quikqApi';
import { 
  SavingsSummary, 
  SavingsTransaction, 
  CardInfo,
  QuikQTransactionResponse,
  QuikQTransactionItem,
  QuikQSubTransactionItem
} from '../services/quikqApi';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`savings-tabpanel-${index}`}
      aria-labelledby={`savings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const Savings: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [savingsData, setSavingsData] = useState<SavingsSummary | null>(null);
  const [transactions, setTransactions] = useState<SavingsTransaction[]>([]);
  const [quikqTransactions, setQuikqTransactions] = useState<QuikQTransactionItem[]>([]);
  const [cards, setCards] = useState<CardInfo[]>([]);


  const [tabValue, setTabValue] = useState(0);
  const [userRole, setUserRole] = useState<string>('USER');
  const [dateRange, setDateRange] = useState(365); // Default to 365 days
  const [stationSortBy, setStationSortBy] = useState<'saved-high-low' | 'saved-low-high' | 'date-today-back' | 'date-back-today'>('saved-high-low');
  const [cardSortBy, setCardSortBy] = useState<'saved-high-low' | 'saved-low-high' | 'date-today-back' | 'date-back-today'>('saved-high-low');
  
  // Pagination state for transactions table
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Date range - default to last 365 days from today
  const endDate = new Date(); // Today
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - dateRange); // Use dateRange state

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  // Sorting functions
  const sortStations = (stations: any[]) => {
    return [...stations].sort((a, b) => {
      switch (stationSortBy) {
        case 'saved-high-low':
          return b.totalSaved - a.totalSaved;
        case 'saved-low-high':
          return a.totalSaved - b.totalSaved;
        case 'date-today-back':
          const dateA = new Date(a.transactionDate);
          const dateB = new Date(b.transactionDate);
          return dateB.getTime() - dateA.getTime();
        case 'date-back-today':
          const dateA2 = new Date(a.transactionDate);
          const dateB2 = new Date(b.transactionDate);
          return dateA2.getTime() - dateB2.getTime();
        default:
          return 0;
      }
    });
  };

  const sortCards = (cards: any[]) => {
    return [...cards].sort((a, b) => {
      switch (cardSortBy) {
        case 'saved-high-low':
          return b.totalSaved - a.totalSaved;
        case 'saved-low-high':
          return a.totalSaved - b.totalSaved;
        case 'date-today-back':
          const dateA = new Date(a.transactionDate);
          const dateB = new Date(b.transactionDate);
          return dateB.getTime() - dateA.getTime();
        case 'date-back-today':
          const dateA2 = new Date(a.transactionDate);
          const dateB2 = new Date(b.transactionDate);
          return dateA2.getTime() - dateB2.getTime();
        default:
          return 0;
      }
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Transform QuikQ transactions to SavingsTransaction format with date filtering
  const transformQuikQToSavingsTransactions = (quikqTransactions: QuikQTransactionItem[]): SavingsTransaction[] => {
    try {
      const savingsTransactions: SavingsTransaction[] = [];
      
      // console.log('🔍 Filtering transactions by date range:', {
      //   startDate: startDate.toLocaleDateString(),
      //   endDate: endDate.toLocaleDateString(),
      //   totalTransactions: quikqTransactions.length
      // });
      
      quikqTransactions.forEach((transactionWrapper, index) => {
        // Handle both direct transaction objects and wrapped transaction objects
        const transaction = 'transaction' in transactionWrapper ? transactionWrapper.transaction : transactionWrapper;
        
        if (!transaction.subTransactions || !Array.isArray(transaction.subTransactions)) {
          console.warn('Invalid transaction structure:', transactionWrapper);
          return;
        }

        // Filter by date - only include transactions within the selected date range
        // Parse QuikQ date format: "2025-04-15-23:54:42" -> "2025-04-15T23:54:42"
        const rawDate = transaction.beginDate || new Date().toISOString();
        // Handle QuikQ date format: "2025-04-15-23:54:42" -> "2025-04-15T23:54:42"
        // Also handle if date is already in ISO format
        let formattedDate = rawDate;
        if (rawDate.includes('-') && rawDate.match(/\d{4}-\d{2}-\d{2}-\d{2}:\d{2}:\d{2}/)) {
          formattedDate = rawDate.replace(/(\d{4}-\d{2}-\d{2})-(\d{2}:\d{2}:\d{2})/, '$1T$2');
        }
        const transactionDate = new Date(formattedDate);
        const isWithinDateRange = transactionDate >= startDate && transactionDate <= endDate;
        
        if (!isWithinDateRange) {
          // console.log(`⏭️ Skipping transaction ${index + 1} - outside date range:`, {
          //   rawDate,
          //   formattedDate,
          //   transactionDate: transactionDate.toLocaleDateString(),
          //   seq: transaction.seq
          // });
          return;
        }

        // console.log(`✅ Processing transaction ${index + 1} (within date range):`, {
        //   seq: transaction.seq,
        //   station: transaction.station,
        //   rawDate,
        //   formattedDate,
        //   transactionDate: transactionDate.toLocaleDateString(),
        //   subTransactionsCount: transaction.subTransactions.length
        // });

        transaction.subTransactions.forEach((subTransWrapper: QuikQSubTransactionItem, subIndex) => {
          try {
            // Handle both direct subTransaction objects and wrapped subTransaction objects
            const subTrans = 'subTransaction' in subTransWrapper ? subTransWrapper.subTransaction : subTransWrapper;
            
            const retailTotal = subTrans.subTotal || 0;
            const adjustedTotal = subTrans.adjSubTotal || 0;
            const savings = retailTotal - adjustedTotal;
            
            // console.log(`🔍 Sub-transaction ${subIndex + 1}:`, {
            //   retailTotal,
            //   adjustedTotal,
            //   savings,
            //   hasSavings: savings > 0
            // });
            
            // Include ALL transactions (not just those with savings > 0)
            // This ensures consistency with admin dashboard which shows all transactions
            // Convert QuikQ date format to ISO format for proper display
            const rawDate = transaction.beginDate || new Date().toISOString();
            // Handle QuikQ date format: "2025-04-15-23:54:42" -> "2025-04-15T23:54:42"
            // Also handle if date is already in ISO format
            let formattedDate = rawDate;
            if (rawDate.includes('-') && rawDate.match(/\d{4}-\d{2}-\d{2}-\d{2}:\d{2}:\d{2}/)) {
              formattedDate = rawDate.replace(/(\d{4}-\d{2}-\d{2})-(\d{2}:\d{2}:\d{2})/, '$1T$2');
            }
            
            const savingsTransaction = {
              transactionId: `${transaction.seq}-${subTrans.subTransNum}`,
              cardNo: transaction.magCard || 'Unknown',
              stationCode: transaction.station || 'Unknown',
              stationName: `Station ${transaction.station || 'Unknown'}`,
              transactionDate: formattedDate, // Use properly formatted date
              fuelType: 'Fuel', // Default since QuikQ doesn't specify fuel type
              gallons: 0, // QuikQ doesn't provide gallons
              retailPrice: retailTotal,
              adjustedPrice: adjustedTotal,
              savings: savings, // Can be 0 or negative - show all transactions
              totalAmount: adjustedTotal,
              location: {
                address: 'N/A',
                city: 'N/A',
                state: 'N/A',
                zip: 'N/A'
              }
            };
            
            savingsTransactions.push(savingsTransaction);
            // console.log('✅ Added savings transaction:', savingsTransaction.transactionId);
          } catch (subTransError) {
            console.warn('Error processing sub-transaction:', subTransError, subTransWrapper);
          }
        });
      });
      
      // console.log('🔍 Total savings transactions created (after date filtering):', savingsTransactions.length);
      return savingsTransactions;
    } catch (error) {
      console.error('Error transforming QuikQ transactions:', error);
      throw new Error('Failed to process transaction data');
    }
  };

  // Fetch savings data using only existing QuikQ endpoints
  const fetchSavingsData = async () => {
    setLoading(true);
    setError('');
    
    try {
      const currentUser = quikqAuthService.getCurrentUser();
      if (!currentUser?.startCode) {
        throw new Error('No authenticated user found. Please log in again.');
      }

      // Check if token is still valid
      try {
        const token = quikqAuthService.getToken();
        if (!token) {
          throw new Error('Authentication token not found. Please log in again.');
        }
        // console.log('🔐 Token found, proceeding with API calls');
      } catch (authError) {
        console.error('🔐 Authentication error:', authError);
        throw new Error('Authentication failed. Please log in again.');
      }

      // Fetch cards first
      try {
        const cardsData = await quikqApiService.getCarrierCards(currentUser.startCode);
        setCards(cardsData);
      } catch (cardsError) {
        console.warn('Failed to fetch cards, continuing without card data:', cardsError);
        setCards([]);
      }

      // Fetch QuikQ transactions and transform them to savings data
      await fetchQuikQTransactions();

    } catch (err) {
      console.error('Error fetching savings data:', err);
      setError('Unable to load savings data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };



  // Fetch QuikQ transactions and transform to savings format
  const fetchQuikQTransactions = async () => {
    try {
      const startDt = startDate.toISOString();
      const endDt = endDate.toISOString();
      
      // Log the date range being used
      // console.log('🔍 Date range for transactions:', {
      //   startDate: startDate.toLocaleDateString(),
      //   endDate: endDate.toLocaleDateString(),
      //   startDt: startDt,
      //   endDt: endDt,
      //   totalDays: Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))
      // });
      
      // Skip the hardcoded transaction ID method since it's not working
      // Go straight to the working startCode method
      let response;
      try {
        // console.log('🔍 Using startCode method directly...');
        response = await quikqApiService.getQuikQTransactionsRickFormatWithStartCode(startDt, endDt);
      } catch (startCodeError) {
        // console.log('🔍 StartCode method failed, trying carrierPartners...', startCodeError);
        try {
          response = await quikqApiService.getQuikQCarrierPartnerTransactions(startDt, endDt);
        } catch (carrierError) {
          // console.log('🔍 Carrier method failed, trying general endpoint...', carrierError);
          response = await quikqApiService.getQuikQTransactionsRickFormat(startDt, endDt);
        }
      }
      
      if (!response) {
        throw new Error('No response received from QuikQ API');
      }
      
      if (response.status !== 'success') {
        throw new Error(`API returned status: ${response.status}`);
      }
      
      // Log the full response structure to understand what we're getting
      // console.log('🔍 Full API response structure:', JSON.stringify(response, null, 2));
      // console.log('🔍 Response data keys:', Object.keys(response.data || {}));
      
      // Check different possible structures
      let transactions: QuikQTransactionItem[] | null = null;
      if (response.data?.report?.transactions) {
        transactions = response.data.report.transactions;
        // console.log('🔍 Found transactions in report.transactions');
      } else if (response.data?.transactions) {
        transactions = response.data.transactions;
        // console.log('🔍 Found transactions in data.transactions');
      } else if (response.data?.data?.transactions) {
        transactions = response.data.data.transactions;
        // console.log('🔍 Found transactions in data.data.transactions');
      } else if (response.transactions) {
        transactions = response.transactions;
        // console.log('🔍 Found transactions in root.transactions');
      } else {
        // console.log('🔍 No transactions found in any expected location');
        // console.log('🔍 Available data structure:', response.data);
        // Handle case where API returns null transactions (no data for date range)
        transactions = [];
      }
      
      if (!transactions || transactions.length === 0) {
        // console.log('🔍 No transactions found or empty transactions array');
        // Set empty data instead of error for no transactions
        setTransactions([]);
        setSavingsData({
          totalSaved: 0,
          totalSpent: 0,
          totalTransactions: 0,
          averageSavings: 0,
          savingsByStation: [],
          savingsByCard: []
        });
        setError('No transactions found for the selected date range.'); // Show info message
        return;
      }
      
      setQuikqTransactions(transactions);
      
      // console.log('🔍 Processing', transactions.length, 'transactions for savings calculation...');
      
      // Transform QuikQ transactions to savings transactions
      const savingsTransactions = transformQuikQToSavingsTransactions(transactions);
      // console.log('🔍 Transformed to', savingsTransactions.length, 'savings transactions');
      
      setTransactions(savingsTransactions);
      
      // Calculate savings summary from filtered QuikQ transactions
      const summary = calculateSavingsFromQuikQTransactions(transactions);
      // console.log('🔍 Calculated savings summary:', summary);
      
      setSavingsData(summary);

      setError(''); // Clear any previous errors
      
    } catch (err) {
      console.error('Error fetching QuikQ transactions:', err);
      
      // Provide better error messages for common issues
      if (err instanceof Error) {
        if (err.message.includes('Authentication failed') || err.message.includes('401')) {
          setError('Your session has expired. Please log in again to continue.');
        } else if (err.message.includes('No transaction data')) {
          setError('No transaction data available for the selected date range.');
        } else {
          setError('Unable to load transaction data. Please try again later.');
        }
      } else {
        setError('Unable to load transaction data. Please try again later.');
      }
    }
  };

  // Calculate savings summary from QuikQ transactions with date filtering
  const calculateSavingsFromQuikQTransactions = (transactions: QuikQTransactionItem[]): SavingsSummary => {
    try {
      let totalSaved = 0;
      let totalSpent = 0;
      let filteredTransactionCount = 0;
      let transactionsWithSavings = 0;
      const stationMap = new Map<string, { saved: number; spent: number; count: number; date: string }>();
      const cardMap = new Map<string, { saved: number; spent: number; count: number; date: string }>();

      // console.log('🔍 Calculating summary for date range:', {
      //   startDate: startDate.toLocaleDateString(),
      //   endDate: endDate.toLocaleDateString(),
      //   totalTransactions: transactions.length
      // });

      transactions.forEach(transactionWrapper => {
        // Handle both direct transaction objects and wrapped transaction objects
        const transaction = 'transaction' in transactionWrapper ? transactionWrapper.transaction : transactionWrapper;
        
        if (!transaction.subTransactions || !Array.isArray(transaction.subTransactions)) {
          return;
        }

        // Filter by date - only include transactions within the selected date range
        // Parse QuikQ date format: "2025-04-15-23:54:42" -> "2025-04-15T23:54:42"
        const rawDate = transaction.beginDate || new Date().toISOString();
        // Handle QuikQ date format: "2025-04-15-23:54:42" -> "2025-04-15T23:54:42"
        // Also handle if date is already in ISO format
        let formattedDate = rawDate;
        if (rawDate.includes('-') && rawDate.match(/\d{4}-\d{2}-\d{2}-\d{2}:\d{2}:\d{2}/)) {
          formattedDate = rawDate.replace(/(\d{4}-\d{2}-\d{2})-(\d{2}:\d{2}:\d{2})/, '$1T$2');
        }
        const transactionDate = new Date(formattedDate);
        const isWithinDateRange = transactionDate >= startDate && transactionDate <= endDate;
        
        if (!isWithinDateRange) {
          return; // Skip transactions outside date range
        }

        filteredTransactionCount++; // Count filtered transactions

        transaction.subTransactions.forEach((subTransWrapper: QuikQSubTransactionItem) => {
          try {
            // Handle both direct subTransaction objects and wrapped subTransaction objects
            const subTrans = 'subTransaction' in subTransWrapper ? subTransWrapper.subTransaction : subTransWrapper;
            
            const retailTotal = subTrans.subTotal || 0;
            const adjustedTotal = subTrans.adjSubTotal || 0;
            const savings = retailTotal - adjustedTotal;
            
            totalSaved += savings;
            totalSpent += adjustedTotal;

            // Count all transactions (not just those with savings > 0)
            // This ensures consistency with admin dashboard
            transactionsWithSavings++;

            // Track by station - ALL transactions in date range
            if (transaction.station) {
              const stationKey = `${transaction.station}-${transaction.seq}-${subTrans.subTransNum}`;
              const station = stationMap.get(stationKey) || { saved: 0, spent: 0, count: 0, date: formattedDate };
              station.saved += savings;
              station.spent += adjustedTotal;
              station.count += 1;
              stationMap.set(stationKey, station);
            }

            // Track by card - ALL transactions in date range
            if (transaction.magCard) {
              const cardKey = `${transaction.magCard}-${transaction.seq}-${subTrans.subTransNum}`;
              const card = cardMap.get(cardKey) || { saved: 0, spent: 0, count: 0, date: formattedDate };
              card.saved += savings;
              card.spent += adjustedTotal;
              card.count += 1;
              cardMap.set(cardKey, card);
            }
          } catch (subTransError) {
            console.warn('Error processing sub-transaction in summary:', subTransError, subTransWrapper);
          }
        });
      });

      // Convert maps to arrays for display - each transaction separately
      const savingsByStation = Array.from(stationMap.entries())
        .map(([stationKey, data]) => {
          const [stationCode, seq, subTransNum] = stationKey.split('-');
          return {
            stationCode,
            stationName: `Station ${stationCode}`,
            totalSaved: data.saved,
            totalSpent: data.spent,
            transactionCount: data.count,
            averageSavings: data.count > 0 ? data.saved / data.count : 0,
            transactionDate: data.date,
          };
        });

      const savingsByCard = Array.from(cardMap.entries())
        .map(([cardKey, data]) => {
          const [cardNo, seq, subTransNum] = cardKey.split('-');
          return {
            cardNo,
            cardType: 'Driver', // Default type
            totalSaved: data.saved,
            totalSpent: data.spent,
            transactionCount: data.count,
            averageSavings: data.count > 0 ? data.saved / data.count : 0,
            transactionDate: data.date,
          };
        });

      // console.log('🔍 Summary calculated:', {
      //   totalSaved,
      //   totalSpent,
      //   filteredTransactionCount,
      //   transactionsWithSavings,
      //   originalTransactionCount: transactions.length,
      //   stationCount: savingsByStation.length,
      //   cardCount: savingsByCard.length,
      //   stations: savingsByStation.map(s => s.stationCode),
      //   cards: savingsByCard.map(c => c.cardNo)
      // });

      return {
        totalSaved,
        totalSpent,
        totalTransactions: filteredTransactionCount, // Use count of ALL transactions in date range
        averageSavings: transactionsWithSavings > 0 ? totalSaved / transactionsWithSavings : 0,
        savingsByStation,
        savingsByCard
      };
    } catch (error) {
      console.error('Error calculating savings summary:', error);
      return {
        totalSaved: 0,
        totalSpent: 0,
        totalTransactions: 0,
        averageSavings: 0,
        savingsByStation: [],
        savingsByCard: []
      };
    }
  };



  useEffect(() => {
    fetchSavingsData();
  }, [dateRange]);



  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Sort stations based on selected criteria (always highest to lowest, except for station name which is A-Z)
  const getSortedStations = () => {
    if (!savingsData?.savingsByStation) return [];
    return sortStations(savingsData.savingsByStation);
  };

  const filteredTransactions = transactions;
  
  // Pagination handlers
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Calculate paginated transactions
  const paginatedTransactions = filteredTransactions.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading) {
    return (
      <Box sx={{ p: 2, pb: { xs: 12, sm: 12, md: 12 }, bgcolor: '#f5f6fa', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} />
          <Typography sx={{ mt: 2 }} color="text.secondary">
            Loading savings data...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: { xs: 2, sm: 3, md: 4 }, 
      pb: { xs: 12, sm: 12, md: 12 }, 
      bgcolor: '#f5f6fa', 
      minHeight: '100vh',
      position: 'relative',
      zIndex: 0,
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '300px',
        height: '300px',
        backgroundImage: 'url(/NSFIcon.png)',
        backgroundSize: 'contain',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        opacity: 0.03,
        zIndex: 0,
        pointerEvents: 'none'
      },
      '& > *': {
        position: 'relative',
        zIndex: 1
      }
    }}>
      {/* Header with Split Watermark - Left: NSFLogo, Right: Empty (for future custom logo) */}
      <Box sx={{ 
        position: 'relative', 
        mb: 3,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: -50,
          left: -50,
          width: '100%',
          height: '200px',
          background: `url('/NSFIcon.png')`,
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          opacity: 0.08,
          zIndex: 0,
          pointerEvents: 'none'
        }
      }}>
        {/* Header */}
        <Paper elevation={1} sx={{ 
          p: { xs: 2, sm: 3, md: 4 }, 
          mb: { xs: 2, sm: 3, md: 4 }, 
          borderRadius: 3, 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' }, 
          justifyContent: 'space-between', 
          boxShadow: 2,
          gap: { xs: 2, sm: 0 },
          position: 'relative',
          zIndex: 1
        }}>
        <Typography fontWeight={700} fontSize={{ xs: 18, sm: 20, md: 24 }}>
          Fuel Savings
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          flexDirection: { xs: 'column', sm: 'row' },
          width: { xs: '100%', sm: 'auto' }
        }}>
          {/* Date Range Selector */}
          <FormControl size="small" sx={{ 
            minWidth: { xs: '100%', sm: 120, md: 150 },
            width: { xs: '100%', sm: 'auto' }
          }}>
            <InputLabel>Date Range</InputLabel>
            <Select
              value={dateRange}
              onChange={(e) => setDateRange(Number(e.target.value))}
              label="Date Range"
            >
              <MenuItem value={7}>Last 7 days</MenuItem>
              <MenuItem value={30}>Last 30 days</MenuItem>
              <MenuItem value={90}>Last 90 days</MenuItem>
              <MenuItem value={180}>Last 6 months</MenuItem>
              <MenuItem value={365}>Last year</MenuItem>
              <MenuItem value={730}>Last 2 years</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="info" sx={{ mb: { xs: 2, sm: 3, md: 4 } }}>
          {error}
        </Alert>
      )}

      {/* Statistika kartice */}
      {savingsData && (
        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(3, 1fr)' },
          gap: { xs: 1, sm: 2, md: 3 }, 
          mb: { xs: 2, sm: 3, md: 4 } 
        }}>
          <Paper elevation={2} sx={{ 
            p: { xs: 1, sm: 2, md: 2.5 }, 
            textAlign: 'center', 
            borderRadius: 2, 
            bgcolor: '#e8f5e9' 
          }}>
            <Typography fontWeight={700} fontSize={{ xs: 14, sm: 18, md: 20 }} color="#43a047">
              {formatCurrency(savingsData.totalSaved)}
            </Typography>
            <Typography fontSize={{ xs: 10, sm: 13, md: 14 }} color="#43a047">Total Saved</Typography>
          </Paper>
          <Paper elevation={2} sx={{ 
            p: { xs: 1, sm: 2, md: 2.5 }, 
            textAlign: 'center', 
            borderRadius: 2, 
            bgcolor: '#e3f2fd' 
          }}>
            <Typography fontWeight={700} fontSize={{ xs: 14, sm: 18, md: 20 }} color="#1976d2">
              {savingsData.totalTransactions}
            </Typography>
            <Typography fontSize={{ xs: 10, sm: 13, md: 14 }} color="#1976d2">Fuel Stops</Typography>
          </Paper>
          <Paper elevation={2} sx={{ 
            p: { xs: 1, sm: 2, md: 2.5 }, 
            textAlign: 'center', 
            borderRadius: 2, 
            bgcolor: '#f3e5f5' 
          }}>
            <Typography fontWeight={700} fontSize={{ xs: 14, sm: 18, md: 20 }} color="#8e24aa">
              {formatCurrency(savingsData.averageSavings)}
            </Typography>
            <Typography fontSize={{ xs: 10, sm: 13, md: 14 }} color="#8e24aa">Avg. Savings</Typography>
          </Paper>
        </Box>
      )}



      {/* Tabs */}
      <Paper elevation={1} sx={{ mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Transactions" />
          <Tab label="By Station" />
          <Tab label="By Card" />
        </Tabs>
      </Paper>

      {/* Transactions Tab */}
      <TabPanel value={tabValue} index={0}>
        {filteredTransactions.length > 0 ? (
          <Paper elevation={1}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Station</TableCell>
                    <TableCell>Card</TableCell>
                    <TableCell>Fuel</TableCell>
                    <TableCell>Retail Price</TableCell>
                    <TableCell>Adjusted Price</TableCell>
                    <TableCell>Savings</TableCell>
                    <TableCell>Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedTransactions.map((transaction) => (
                    <TableRow key={transaction.transactionId}>
                      <TableCell>{formatDate(transaction.transactionDate)}</TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {transaction.stationName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {transaction.location.city}, {transaction.location.state}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          icon={<CreditCard />}
                          label={transaction.cardNo.slice(-4)}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{transaction.fuelType}</TableCell>
                      <TableCell>{formatCurrency(transaction.retailPrice)}</TableCell>
                      <TableCell>{formatCurrency(transaction.adjustedPrice)}</TableCell>
                      <TableCell>
                        <Chip 
                          icon={<TrendingUp />}
                          label={formatCurrency(transaction.savings)}
                          size="small"
                          color="success"
                        />
                      </TableCell>
                      <TableCell>{formatCurrency(transaction.totalAmount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredTransactions.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Rows per page:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`}
            />
          </Paper>
        ) : (
          <Box sx={{ textAlign: 'center', mt: 8 }}>
            <Paper elevation={0} sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f6fa', borderRadius: '50%', width: 64, height: 64, mb: 2 }}>
              <LocalGasStation sx={{ fontSize: 36, color: '#e0e0e0' }} />
            </Paper>
            <Typography fontWeight={700} fontSize={16} color="#212121" mb={0.5}>No fuel transactions yet</Typography>
            <Typography fontSize={14} color="#757575">Start using your Quikq fuel card to see your savings here!</Typography>
          </Box>
        )}
      </TabPanel>

      {/* By Station Tab */}
      <TabPanel value={tabValue} index={1}>
        {savingsData?.savingsByStation.length ? (
          <Box>
            {/* Sort Controls */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              gap: 2,
              mb: 3,
              p: 2,
              bgcolor: '#f8f9fa',
              borderRadius: 2
            }}>
              <Typography variant="subtitle2" fontWeight={600} color="#1976d2">
                Sort by:
              </Typography>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <Select
                  value={stationSortBy}
                  onChange={(e) => setStationSortBy(e.target.value as any)}
                  sx={{ '& .MuiSelect-select': { py: 1 } }}
                >
                  <MenuItem value="saved-high-low">Saved (High → Low)</MenuItem>
                  <MenuItem value="saved-low-high">Saved (Low → High)</MenuItem>
                  <MenuItem value="date-today-back">Date (Today → Past)</MenuItem>
                  <MenuItem value="date-back-today">Date (Past → Today)</MenuItem>
                </Select>
              </FormControl>
            </Box>
            
            {/* Station Cards */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {getSortedStations().map((station, index) => (
                <Card elevation={1} key={`${station.stationCode}-${index}`}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <LocalGasStation sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6" fontWeight={600}>
                      {station.stationName}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">Total Saved:</Typography>
                    <Typography variant="body2" fontWeight={600} color="success.main">
                      {formatCurrency(station.totalSaved)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">Total Spent:</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {formatCurrency(station.totalSpent)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">Transactions:</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {station.transactionCount}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Avg. Savings:</Typography>
                    <Typography variant="body2" fontWeight={600} color="success.main">
                      {formatCurrency(station.averageSavings)}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            ))}
            </Box>
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography color="text.secondary">No station data available</Typography>
          </Box>
        )}
      </TabPanel>

      {/* By Card Tab */}
      <TabPanel value={tabValue} index={2}>
        {savingsData?.savingsByCard.length ? (
          <Box>
            {/* Sort Controls */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              gap: 2,
              mb: 3,
              p: 2,
              bgcolor: '#f8f9fa',
              borderRadius: 2
            }}>
              <Typography variant="subtitle2" fontWeight={600} color="#1976d2">
                Sort by:
              </Typography>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <Select
                  value={cardSortBy}
                  onChange={(e) => setCardSortBy(e.target.value as any)}
                  sx={{ '& .MuiSelect-select': { py: 1 } }}
                >
                  <MenuItem value="saved-high-low">Saved (High → Low)</MenuItem>
                  <MenuItem value="saved-low-high">Saved (Low → High)</MenuItem>
                  <MenuItem value="date-today-back">Date (Today → Past)</MenuItem>
                  <MenuItem value="date-back-today">Date (Past → Today)</MenuItem>
                </Select>
              </FormControl>
            </Box>
            
            {/* Card Cards */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {sortCards(savingsData.savingsByCard).map((card, index) => (
              <Card elevation={1} key={`${card.cardNo}-${index}`}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CreditCard sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6" fontWeight={600}>
                      Card {card.cardNo.slice(-4)}
                    </Typography>
                    <Chip 
                      label={card.cardType}
                      size="small"
                      color="secondary"
                      sx={{ ml: 1 }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">Total Saved:</Typography>
                    <Typography variant="body2" fontWeight={600} color="success.main">
                      {formatCurrency(card.totalSaved)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">Total Spent:</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {formatCurrency(card.totalSpent)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">Transactions:</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {card.transactionCount}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Avg. Savings:</Typography>
                    <Typography variant="body2" fontWeight={600} color="success.main">
                      {formatCurrency(card.averageSavings)}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            ))}
            </Box>
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography color="text.secondary">No card data available</Typography>
          </Box>
        )}
      </TabPanel>
    </Box>
  );
};

export default Savings; 